/**
 * The tech's own duration for a block (section 9.4; ADR 0059): logged instead
 * of the counted time, or cleared to go back to it. The popup's More row
 * opens it in the popup's right pane (ADR 0193); Cancel goes back.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";

import { ApiError } from "../../api/client";
import { useSession } from "../../app/session-context";
import { formatDuration } from "../../lib/durations";
import type { BlockView } from "./model";

const secondary =
  "h-10 cursor-pointer self-start rounded-pill border border-line bg-panel px-4 text-[13.5px] font-semibold hover:bg-panel-2 disabled:cursor-not-allowed disabled:opacity-50";
// Autotask takes at most 24 hours in one time entry.
const MAX_MINUTES = 24 * 60;

function refusal(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) {
      return "This block can't change any more. Reload to see where it is now.";
    }
    if (error.status === 403) {
      return "You can only change your own blocks.";
    }
    return error.fieldErrors[0]?.message ?? error.detail ?? error.message;
  }
  return error instanceof Error ? error.message : "Something went wrong.";
}

export function ChangeDuration({
  block,
  onDone,
  onCancel,
}: {
  block: BlockView;
  /** Told what happened, once the day shows it (#247). */
  onDone: (message: string) => void;
  /** Cancel: back to what the popup showed before (ADR 0193). */
  onCancel: () => void;
}) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const minutesId = useId();
  const [minutes, setMinutes] = useState(String(Math.max(1, Math.round(block.seconds / 60))));
  const save = useMutation({
    mutationFn: (seconds: number | null) => api.setBlockDuration(block.id, seconds),
    onSuccess: async (_, seconds) => {
      // Closed once the day shows it, with focus moved off the form (#247).
      await queryClient.invalidateQueries({ queryKey: ["day"] });
      onDone(
        seconds === null
          ? `Back to the counted ${formatDuration(block.countedSeconds)}.`
          : `Duration set to ${formatDuration(seconds)}.`,
      );
      // The Log form's preview (title, notes, hours) is built from the block's
      // activities: ask for it again, or it shows the old block (#224).
      await queryClient.invalidateQueries({ queryKey: ["submission-preview"] });
    },
  });
  const chosen = Number(minutes);
  const valid = Number.isInteger(chosen) && chosen >= 1 && chosen <= MAX_MINUTES;

  return (
    <form
      aria-label="Change the duration"
      className="flex flex-col gap-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid) {
          save.mutate(chosen * 60);
        }
      }}
    >
      <div className="flex flex-col gap-[5px]">
        <label htmlFor={minutesId} className="text-xs font-semibold text-muted">
          Minutes
        </label>
        <input
          id={minutesId}
          type="number"
          inputMode="numeric"
          min={1}
          max={MAX_MINUTES}
          step={1}
          value={minutes}
          onChange={(event) => {
            setMinutes(event.target.value);
          }}
          className="h-10 w-32 rounded-input border border-line bg-card px-3 text-[13.5px] text-text"
        />
        <span className="text-xs text-muted">{`Counted: ${formatDuration(block.countedSeconds)}`}</span>
      </div>
      {save.isError ? (
        <p role="alert" className="text-[13px] text-status-needs-client-fg">
          {refusal(save.error)}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={!valid || save.isPending}
          className="h-10 cursor-pointer rounded-pill bg-cta-bg px-5 text-[13.5px] font-bold text-cta-fg disabled:cursor-not-allowed disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : valid ? `Use ${formatDuration(chosen * 60)}` : "Use this"}
        </button>
        {block.overridden ? (
          <button
            type="button"
            disabled={save.isPending}
            onClick={() => {
              save.mutate(null);
            }}
            className={secondary}
          >
            {`Back to the counted ${formatDuration(block.countedSeconds)}`}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onCancel}
          className={secondary}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
